import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { ArrowDownLeft, Clock, Landmark, Loader2 } from 'lucide-react'

import type { BalanceEvent, ReconcileStatus } from '@/lib/api/balance-event-api'
import type { SalesOrder } from '@/lib/api/sales-order-api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    useAssignBalanceEvent,
    useBalanceEvents,
    useIgnoreBalanceEvent,
} from '@/hooks/use-balance-event-query'
import { OrderSearchSelect } from '@/components/forms/order-search-select'
import { formatDateTime, formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/accounting/reconciliation',
)({
    component: ReconciliationPage,
})

function statusLabel(s: ReconcileStatus): string {
    if (s === 'matched') return m.reconcile_status_matched()
    if (s === 'ignored') return m.reconcile_status_ignored()
    return m.reconcile_status_unmatched()
}

function ReconciliationPage() {
    const [status, setStatus] = useState<ReconcileStatus | 'all'>('unmatched')
    const [dateFrom, setDateFrom] = useState('')
    const [dateTo, setDateTo] = useState('')
    const [assigning, setAssigning] = useState<BalanceEvent | null>(null)
    const [ignoring, setIgnoring] = useState<BalanceEvent | null>(null)

    const { data: listRes } = useBalanceEvents({
        type: 'in',
        status,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        per_page: 50,
    })
    const rows = listRes?.data ?? []

    return (
        <div className="space-y-4 p-4">
            <div>
                <h1 className="text-xl font-semibold">{m.reconcile_title()}</h1>
                <p className="text-sm text-muted-foreground">
                    {m.reconcile_subtitle()}
                </p>
            </div>

            {/* Filter bar */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                    <Label className="text-xs">
                        {m.reconcile_filter_status()}
                    </Label>
                    <Select
                        value={status}
                        onValueChange={(v) =>
                            setStatus(v as ReconcileStatus | 'all')
                        }
                    >
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="unmatched">
                                {m.reconcile_status_unmatched()}
                            </SelectItem>
                            <SelectItem value="matched">
                                {m.reconcile_status_matched()}
                            </SelectItem>
                            <SelectItem value="ignored">
                                {m.reconcile_status_ignored()}
                            </SelectItem>
                            <SelectItem value="all">
                                {m.reconcile_filter_all()}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs">
                        {m.reconcile_filter_from()}
                    </Label>
                    <Input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                    />
                </div>
                <div>
                    <Label className="text-xs">{m.reconcile_filter_to()}</Label>
                    <Input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                    />
                </div>
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.reconcile_col_date()}</TableHead>
                        <TableHead className="text-right">
                            {m.reconcile_col_amount()}
                        </TableHead>
                        <TableHead>{m.reconcile_col_content()}</TableHead>
                        <TableHead>{m.reconcile_col_account()}</TableHead>
                        <TableHead className="text-right">
                            {m.reconcile_col_actions()}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={5}
                                className="text-center text-muted-foreground py-8"
                            >
                                {m.reconcile_list_empty()}
                            </TableCell>
                        </TableRow>
                    )}
                    {rows.map((row) => (
                        <TableRow key={row.id}>
                            <TableCell>
                                {row.transacted_at.slice(0, 10)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                                {formatVnd(row.amount)}
                            </TableCell>
                            <TableCell className="max-w-[20rem] truncate">
                                {row.content}
                            </TableCell>
                            <TableCell>{row.account_number}</TableCell>
                            <TableCell className="text-right space-x-1">
                                {row.reconcile_status === 'unmatched' ? (
                                    <>
                                        <Button
                                            size="sm"
                                            onClick={() => setAssigning(row)}
                                        >
                                            {m.reconcile_assign()}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setIgnoring(row)}
                                        >
                                            {m.reconcile_ignore()}
                                        </Button>
                                    </>
                                ) : (
                                    <Badge variant="secondary">
                                        {statusLabel(row.reconcile_status)}
                                    </Badge>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {assigning && (
                <AssignDialog
                    event={assigning}
                    onClose={() => setAssigning(null)}
                />
            )}
            {ignoring && (
                <IgnoreDialog
                    event={ignoring}
                    onClose={() => setIgnoring(null)}
                />
            )}
        </div>
    )
}

function AssignDialog({
    event,
    onClose,
}: {
    event: BalanceEvent
    onClose: () => void
}) {
    const [order, setOrder] = useState<SalesOrder | null>(null)
    const assignMut = useAssignBalanceEvent()

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{m.reconcile_assign_title()}</DialogTitle>
                </DialogHeader>
                <div className="min-w-0 space-y-4">
                    {/* Card khoản CK — nổi bật số tiền tiền-về (emerald) + icon chip */}
                    <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900 dark:bg-emerald-950/40">
                        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                            <ArrowDownLeft className="size-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-xs font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                                {m.reconcile_assign_incoming()}
                            </div>
                            <div className="font-mono text-lg font-bold text-emerald-700 tabular-nums dark:text-emerald-300">
                                {formatVnd(event.amount)}
                            </div>
                            <div className="mt-1 line-clamp-2 text-sm text-muted-foreground [overflow-wrap:anywhere]">
                                {event.content}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                                <span className="inline-flex items-center gap-1">
                                    <Landmark className="size-3" />
                                    {event.account_number}
                                </span>
                                <span className="inline-flex items-center gap-1">
                                    <Clock className="size-3" />
                                    {formatDateTime(event.transacted_at)}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div>
                        <Label className="text-sm font-medium">
                            {m.reconcile_assign_pick_order()}
                        </Label>
                        <div className="mt-1.5">
                            <OrderSearchSelect
                                value={order}
                                onChange={setOrder}
                            />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        disabled={assignMut.isPending}
                        onClick={onClose}
                    >
                        {m.common_cancel()}
                    </Button>
                    <Button
                        disabled={!order || assignMut.isPending}
                        onClick={async () => {
                            if (!order) return
                            await assignMut.mutateAsync({
                                id: event.id,
                                orderId: order.id,
                            })
                            onClose()
                        }}
                    >
                        {assignMut.isPending && (
                            <Loader2 className="size-4 animate-spin" />
                        )}
                        {m.reconcile_assign_confirm()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

function IgnoreDialog({
    event,
    onClose,
}: {
    event: BalanceEvent
    onClose: () => void
}) {
    const [note, setNote] = useState('')
    const ignoreMut = useIgnoreBalanceEvent()

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{m.reconcile_ignore_title()}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="rounded border p-2 text-sm">
                        <div className="font-mono font-semibold">
                            {formatVnd(event.amount)}
                        </div>
                        <div className="text-muted-foreground truncate">
                            {event.content}
                        </div>
                    </div>
                    <div>
                        <Label>{m.reconcile_ignore_note()}</Label>
                        <Input
                            value={note}
                            placeholder={m.reconcile_ignore_note_placeholder()}
                            onChange={(e) => setNote(e.target.value)}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        disabled={ignoreMut.isPending}
                        onClick={async () => {
                            await ignoreMut.mutateAsync({
                                id: event.id,
                                note: note || undefined,
                            })
                            onClose()
                        }}
                    >
                        {m.reconcile_ignore_confirm()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
